/**
 * Primary front-end script.
 *
 * Primary JavaScript file. Any includes or anything imported should
 * be filtered through this file and eventually saved back into the
 * `/dist/js/app.js` file.
 *
 * @package   Classe7
 * @author    Matière Noire <contact@matierenoire.io>
 * @copyright 2018 Matière Noire
 * @license   https://www.gnu.org/licenses/gpl-2.0.html GPL-2.0-or-later
 * @link      https://github.com/matiere-noire/classe7
 */

import ScrollMagic from 'scrollmagic'
import cssVars from 'css-vars-ponyfill'
import $ from 'jquery';
import 'slick-carousel';

if (typeof window.jQuery !== 'undefined' && typeof $.fn.slick === 'function' && typeof window.jQuery.fn.slick !== 'function') {
	window.jQuery.fn.slick = $.fn.slick;
}
/**
 * A ponyfill that provides client-side support for CSS custom properties (aka “CSS variables”) in legacy and modern browsers.
 * https://jhildenbiddle.github.io/css-vars-ponyfill/#/
 */
cssVars({})

/**
 * Sticky header
 */

// ScrollMagic controler
const controller = new ScrollMagic.Controller()

function isScrollMagicController(scrollController) {
	return scrollController
		&& typeof scrollController.addScene === 'function'
		&& typeof scrollController.removeScene === 'function'
}

function isScrollMagicScene(scrollScene) {
	return scrollScene
		&& typeof scrollScene.addTo === 'function'
		&& typeof scrollScene.destroy === 'function'
}

// TMAEX-4463 / Sentry 7790525080 : menu mobile ansemble.fr peut invalider scène/contrôleur.
function addScrollMagicSceneToController(scrollScene, scrollController) {
	if (!isScrollMagicScene(scrollScene) || !isScrollMagicController(scrollController)) {
		return scrollScene
	}

	try {
		scrollScene.addTo(scrollController)
	} catch (error) {
		// Scène ou contrôleur déjà détruit / DOM détaché.
	}

	return scrollScene
}

function destroyScrollMagicScene(scrollScene) {
	if (!isScrollMagicScene(scrollScene)) {
		return
	}

	try {
		scrollScene.destroy(true)
	} catch (error) {
		// Scène déjà détruite ou contrôleur invalide.
	}
}

// Panneaux accordéon / modules / tabulations / tours (masqués avant reveal si besoin).
const scrollMagicRevealPanelAncestorSelectors = [
	'.classe7-accordion-content',
	'.wp-block-classe7-module-description',
	'.wp-block-classe7-tabulation-description',
	'.wp-group-classe7-tour-one-description',
]

// TMAEX-4518 / Sentry 7797484467 : ne pas lier ScrollMagic aux panneaux masqués ni aux cibles GSAP.
function isScrollMagicRevealTriggerElement(triggerElement) {
	if (!triggerElement || !triggerElement.isConnected) {
		return false
	}

	if (triggerElement.hasAttribute('hidden') || triggerElement.hasAttribute('data-animation')) {
		return false
	}

	if (typeof triggerElement.closest === 'function') {
		if (triggerElement.closest('[hidden]')) {
			return false
		}

		for (let i = 0; i < scrollMagicRevealPanelAncestorSelectors.length; i++) {
			const panel = triggerElement.closest(scrollMagicRevealPanelAncestorSelectors[i])
			if (panel && panel.hasAttribute('hidden')) {
				return false
			}
		}
	}

	return true
}

function initScrollMagicRevealScenes(scrollController) {
	const revealElements = document.querySelectorAll(
		'.is-style-circle-mask, .is-style-reveal-on-scroll, .wp-block-media-text__media:not([data-animation]), .classe7-icon-hover, .wp-block-embed, .wp-block-classe7-partenaires .wp-block-image, .wp-block-classe7-titre-chiffres-clefs .wp-block-column:not([data-animation]), .job-offers__service, .wp-block-classe7-modules .wp-block-column:not([data-animation])'
	)
	for (let i = 0; i < revealElements.length; i++) {
		const triggerElement = revealElements[i]
		// Sentry 7797484467 : skip hidden panels and GSAP targets.
		if (!isScrollMagicRevealTriggerElement(triggerElement)) {
			continue
		}

		const revealScene = new ScrollMagic.Scene({
			triggerElement,
			offset: 50,
			triggerHook: 0.8
		})
			.setClassToggle(triggerElement, 'visible')

		addScrollMagicSceneToController(revealScene, scrollController)
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const wpadminbar = document.getElementById('wpadminbar')
	const offset = 50

	const stickyHeader = document.getElementById('sticky-header')
	if (!stickyHeader) {
		return
	}

	// TMAEX-4375 / TMAEX-3644 : en dessous de 950px le header est sticky en CSS.
	// ScrollMagic setPin en position:fixed casse le header mobile (Sentry 7778633715).
	const mobileHeaderQuery = window.matchMedia('(max-width: 950px)')

	const opacityHeader = getComputedStyle(document.documentElement).getPropertyValue('--header-opacity')
	const needsMinOpacity = 0.5 > parseFloat(opacityHeader)

	const computeStickyTop = () => {
		if (wpadminbar) {
			return wpadminbar.offsetHeight + offset
		}

		return offset
	}

	const syncMobileHeaderVars = () => {
		const adminOffset = wpadminbar ? wpadminbar.offsetHeight : 0
		document.documentElement.style.setProperty('--admin-bar-offset', `${adminOffset}px`)
		document.documentElement.style.setProperty('--app-header-height', `${stickyHeader.offsetHeight}px`)
	}

	const syncMobileHeaderOpacity = () => {
		if (!needsMinOpacity) {
			return
		}

		stickyHeader.classList.toggle('min-opacity', window.scrollY >= offset)
	}

	const clearPinStyles = () => {
		stickyHeader.style.position = ''
		stickyHeader.style.top = ''
		stickyHeader.style.left = ''
		stickyHeader.style.bottom = ''
		stickyHeader.style.right = ''
		stickyHeader.style.margin = ''
		stickyHeader.style.width = ''
	}

	let scene = null

	const enableDesktopPin = () => {
		if (scene || mobileHeaderQuery.matches) {
			return
		}

		stickyHeader.style.top = `${computeStickyTop()}px`

		scene = new ScrollMagic.Scene({ offset: offset }).setPin(stickyHeader)

		if (needsMinOpacity) {
			scene.setClassToggle(stickyHeader, 'min-opacity')
		}

		addScrollMagicSceneToController(scene, controller)
	}

	const disableDesktopPin = () => {
		if (scene) {
			destroyScrollMagicScene(scene)
			scene = null
		}

		clearPinStyles()
	}

	const applyHeaderMode = () => {
		if (mobileHeaderQuery.matches) {
			disableDesktopPin()
			syncMobileHeaderVars()
			syncMobileHeaderOpacity()
			return
		}

		enableDesktopPin()
	}

	applyHeaderMode()

	window.addEventListener('scroll', () => {
		if (mobileHeaderQuery.matches) {
			syncMobileHeaderOpacity()
		}
	}, { passive: true })

	if (typeof mobileHeaderQuery.addEventListener === 'function') {
		mobileHeaderQuery.addEventListener('change', applyHeaderMode)
	} else if (typeof mobileHeaderQuery.addListener === 'function') {
		mobileHeaderQuery.addListener(applyHeaderMode)
	}
})

/**
 * Open menu connexion (secondary)
 */
const secondaryNavToggleButton = document.getElementById('secondary-nav-toggle')

if (secondaryNavToggleButton) {
	const secondaryMenu = document.querySelector('.app-subheader .menu--secondary')

	secondaryNavToggleButton.addEventListener('click', () => {
		secondaryMenu?.classList.toggle('menu--secondary-open')
	})
}

/**
 * Open menu
 */
const primaryNavToggleButton = document.getElementById('primary-nav-toggle')

if (primaryNavToggleButton) {
	const primaryMenu = document.querySelector('.app-header .menu--primary')

	primaryNavToggleButton.addEventListener('click', function() {
		this.classList.toggle('active')
		primaryMenu?.classList.toggle('open')
	})
}

const menuItems = document.querySelectorAll('.app-header li.has-children')
Array.prototype.forEach.call(menuItems, function(el, i) {
	let timer

	el.addEventListener('mouseover', function(event) {
		el.classList.add('open')
		clearTimeout(timer)
	})
	el.addEventListener('mouseout', function(event) {
		timer = setTimeout(function(event) {
			el.classList.remove('open')
		}, 500)
	})

	el.querySelector('a.menu__link').addEventListener('click', function(event) {
		event.preventDefault()
		if ('has-children' === this.parentNode.className) {
			this.parentNode.className = 'has-children open'
			this.parentNode.querySelector('a').setAttribute('aria-expanded', 'true')
		} else {
			this.parentNode.className = 'has-children'
			this.parentNode.querySelector('a').setAttribute('aria-expanded', 'false')
		}
	})
})

/**
 * Gestion du hover des actualité
 * @type {NodeListOf<Element>}
 */
const typePost = document.querySelectorAll('.entry--type-post')
if (typePost) {
	Array.prototype.forEach.call(typePost, function(el, i) {
		const link = el.querySelector('.entry__permalink')
		if (link){
			link.addEventListener('mouseenter', function() {
				el.classList.add('hover')
			})
			link.addEventListener('mouseleave', function() {
				el.classList.remove('hover')
			})
		}
	})
}
/**
 * A simple immediately-invoked function expression to kick-start
 * things and encapsulate our code.
 *
 * @since  1.0.0
 * @access public
 * @return void
 */
;(function() {
	// Block accordéons — toggles only (aria-controls), not nested buttons in panel content (TMAEX-4471).
	const allButton = document.querySelectorAll(
		'.wp-block-classe7-accordion > button[aria-controls], .wp-block-classe7-accordion > div > button[aria-controls]'
	)

	const getModuleTabContainer = (link) => link?.parentElement?.parentElement

	const setModuleTabState = (link, className, add = true) => {
		const container = getModuleTabContainer(link)
		if (container) {
			container.classList[add ? 'add' : 'remove'](className)
		}
	}

	if (allButton) {
		const getAccordionContent = (accordionButton) => {
			const nextSibling = accordionButton.nextElementSibling
			if (nextSibling?.classList?.contains('classe7-accordion-content')) {
				return nextSibling
			}

			const controlsId = accordionButton.getAttribute('aria-controls')
			if (!controlsId) {
				return null
			}

			return document.getElementById(controlsId)
		}

		const closeAllAccordions = () => {
			Array.prototype.slice.call(allButton).forEach(function(accordion) {
				const accContent = getAccordionContent(accordion)
				if (accContent) {
					accContent.setAttribute('hidden', '')
				}
				accordion.setAttribute('aria-expanded', 'false')
			})
		}
		Array.prototype.slice.call(allButton).forEach(function(accordion) {
			accordion.addEventListener('click', function(event) {
				// Check if the current toggle is expanded.
				const isExpanded = 'true' === accordion.getAttribute('aria-expanded')

				const accContent = getAccordionContent(accordion)

				if (!isExpanded) {
					// On ferme tous les accordéons de la page quand on en ouvre un
					closeAllAccordions()
				}

				// Set the expanded state on the triggering element
				accordion.setAttribute('aria-expanded', isExpanded ? 'false' : 'true')

				// Hide the accordion sections, using aria-controls to specify the desired section
				if (accContent) {
					if (isExpanded) {
						accContent.setAttribute('hidden', '')
					} else {
						accContent.removeAttribute('hidden')
					}
				}

				event.preventDefault()
			})
		})
	}

	// Accorédons des modules
	const allModules = document.querySelectorAll('.wp-block-classe7-modules, .wp-block-classe7-modules-sticky, .wp-block-classe7-tabulations, .wp-block-classe7-tours')

	const getTabContentFromLink = (tabLink) => {
		const href = tabLink.getAttribute('href')
		if (!href || '#' !== href.charAt(0)) {
			return null
		}

		return document.getElementById(href.slice(1))
	}

	Array.prototype.forEach.call(allModules, function(module) {
		const allTabLink = module.querySelectorAll('a[href^="#"]')
		const allTabContent = []

		Array.prototype.forEach.call(allTabLink, function(tabLink) {
			const tabContent = getTabContentFromLink(tabLink)
			if (tabContent && !allTabContent.includes(tabContent)) {
				allTabContent.push(tabContent)
			}
		})

		const validTabLinks = Array.prototype.filter.call(allTabLink, function(tabLink) {
			return Boolean(getTabContentFromLink(tabLink))
		})

		const closeAllTab = () => {
			allTabContent.forEach(function(moduleDescription) {
				if (moduleDescription) {
					moduleDescription.setAttribute('hidden', '')
				}
			})
			validTabLinks.forEach(function(tabLink) {
				if (!tabLink) {
					return
				}
				tabLink.setAttribute('aria-expanded', 'false')
				setModuleTabState(tabLink, 'current', false)
			})
		}

		const initialiseTab = () => {
			closeAllTab()
			const hash = window.location.hash

			let currentTab = allTabContent[0]
			let currentTabLink = validTabLinks[0]
			if (hash) {
				const currentTabFromHash = allTabContent.find(function(tabContent) {
					return `#${tabContent.getAttribute('id')}` === hash
				})
				if (currentTabFromHash) {
					currentTab = currentTabFromHash
				}

				const currentTabLinkFromHash = Array.prototype.find.call(allTabLink, function(tabLink) {
					return tabLink.getAttribute('href') === hash
				})
				if (currentTabLinkFromHash) {
					currentTabLink = currentTabLinkFromHash
				}
			}

			if (!currentTab) {
				return
			}

			currentTab.removeAttribute('hidden')
			if (currentTabLink) {
				currentTabLink.setAttribute('aria-expanded', 'true')
				setModuleTabState(currentTabLink, 'current')
			}
		}

		if (0 < allTabContent.length) {
			initialiseTab()
		}

		validTabLinks.forEach(function(accordion) {
			accordion.addEventListener('click', function(event) {
				const accContent = getTabContentFromLink(accordion)
				if (!accContent) {
					return
				}

				closeAllTab()
				accordion.setAttribute('aria-expanded', 'true')
				setModuleTabState(accordion, 'current')
				accContent.removeAttribute('hidden')

				jQuery('html, body').animate(
					{
						scrollTop: accContent.offsetTop
					},
					666
				)
				event.preventDefault()
			})
			accordion.addEventListener('mouseenter', function() {
				setModuleTabState(accordion, 'modules-hover')
			})
			accordion.addEventListener('mouseleave', function() {
				setModuleTabState(accordion, 'modules-hover', false)
			})
		})
	})
})()

/**
 * Scroll reveal (TMAEX-4518 / Sentry 7797484467) — après init onglets/accordéons pour respecter [hidden].
 */
initScrollMagicRevealScenes(controller)

// Block slider
function applyBlockCarouselAutoplay($carousel, slickInstance) {
	if (!$carousel || !$carousel.length) {
		return
	}

	const setAutoplay = (slick) => {
		if (!slick || typeof slick.slickSetOption !== 'function') {
			return
		}
		slick.slickSetOption('autoplaySpeed', 7000, true)
		slick.slickPlay()
	}

	if (slickInstance) {
		setAutoplay(slickInstance)
		return
	}

	if (!$carousel.hasClass('slick-initialized')) {
		return
	}

	const elSlick = $carousel.get(0)?.slick
	if (elSlick) {
		setAutoplay(elSlick)
		return
	}

	const $slickHost =
		typeof window.jQuery !== 'undefined'
			? window.jQuery($carousel[0])
			: $carousel
	const dataSlick = $slickHost.data('slick')
	if (
		!dataSlick ||
		typeof dataSlick.slickSetOption !== 'function'
	) {
		return
	}

	$slickHost
		.slick('slickSetOption', 'autoplaySpeed', 7000, true)
		.slick('slickPlay')
}

$(function() {
	const carousel$ =
		typeof window.jQuery !== 'undefined' && typeof window.jQuery.fn?.slick === 'function'
			? window.jQuery
			: $

	if (typeof carousel$.fn.slick !== 'function') {
		return
	}

	const carouselSelector = '.wp-block-cb-carousel'
	const $carousels = carousel$(carouselSelector)

	if (0 === $carousels.length) {
		return
	}

	$carousels.filter('.slick-initialized').each(function() {
		applyBlockCarouselAutoplay(carousel$(this))
	})

	$carousels.on('init', function(event, slick) {
		applyBlockCarouselAutoplay(carousel$(this), slick)
	})
})
